import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  ButtonProps,
  Checkbox,
  Dropdown,
  DropdownProps,
  Form,
  FormField,
  FormGroup,
  Icon,
  Radio,
  RadioProps,
} from "semantic-ui-react";

import moment, { Moment } from "moment";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";
import ModInfo from "react-lib/apps/common/ModInfo";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import TimeComboBox from "react-lib/apps/common/TimeComboBox";

import { ADMIN_NAME_LIST } from "react-lib/apps/USER/CardSettingDivision";

import ButtonMode from "./ButtonMode";
import ColorPicker from "./ColorPicker";
import { HolidayResult, HolidaysType } from "./HolidayPopup";
import LeaveTypeTooltip from "./LeaveTypeTooltip";
import ModTimeShiftHistory from "./ModTimeShiftHistory";
import SelectMonthYear from "./SelectMonthYear";
import TableTimeShift, { SlotInfoType } from "./TableTimeShift";

import {
  ACTIONS,
  ActionSaveType,
  ApplyNewTimeParamsType,
  ApproveTimeParamsType,
  ASSIGN_SHIFT_TIME_OPTIONS,
  AssignType,
  BTN_ACTS,
  ClockRecordParamsType,
  DuplicateEmployeeType,
  EditModeType,
  EditRegularWorkParams,
  EventsTimeShiftType,
  getDaysOfMonth,
  HOLIDAY_TAG,
  OnEvent,
  OpenHistoryParamsType,
  PickedState,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
  StoreSlotType,
  WorkShiftSettingType,
  WorkShiftTemplateType,
  WorkShiftType,
} from "./sequence/SetupWorkSchedule";

import "./CardSetupWorkSchedule.scss";

// Types
type CardSetupWorkScheduleProps = Pick<
  PickedState,
  | "adminMenuDivisions"
  | "buttonLoadCheck"
  | "errorMessage"
  | "searchedItemListWithKey"
  | "selectedDivision"
> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  SetupWorkScheduleSequence: State["SetupWorkScheduleSequence"];
  divisionOptions: Record<string, any>[];
};

type ModConfirmType = {
  data: Record<string, any> | null;
  type: "APPROVE" | "DUPLICATE" | "SEARCH" | "SLOT_OFF" | "TEMPLATE";
};

type AddSlotOffType = { employee: number; index: number; startDate: string };

type AddEmployeeModeType = "DEPARTMENT" | "EMPLOYEE";

const DEFAULT_TEMPLATE: WorkShiftTemplateType = {
  color_code: "#BFEAFB",
  end_time: "",
  name: "",
  shift_type: "D",
  start_time: "",
};

const CardSetupWorkSchedule = (props: CardSetupWorkScheduleProps) => {
  const [showSummaryColumn, setShowSummaryColumn] = useState(true);
  const [viewMonth, setViewMonth] = useState<Moment>(() => moment());

  // === View & Display Mode ===
  const [isViewShiftCodeSummary, setIsViewShiftCodeSummary] = useState<boolean>(false);
  const [mode, setMode] = useState<EditModeType>("SELECT");

  // === Employee Management ===
  const [addEmployeeMode, setAddEmployeeMode] = useState<"" | AddEmployeeModeType>("");
  const [selectedEmployee, setSelectedEmployee] = useState<Record<string, any> | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [modRemoveEmployee, setModRemoveEmployee] = useState<number | null>(null);
  const [selectedDuplicateEmployee, setSelectedDuplicateEmployee] =
    useState<DuplicateEmployeeType | null>(null);
  const [isSearchByAffiliate, setIsSearchByAffiliate] = useState<boolean>(false);

  // === Shift Assignment ===
  const [assignType, setAssignType] = useState<"" | "CUSTOM" | "OFF" | "TEMPLATE">("");
  const [selectedTemplate, setSelectedTemplate] = useState<
    (WorkShiftTemplateType & { isConfirmed?: boolean }) | null
  >(null);

  const [modConfirm, setModConfirm] = useState<ModConfirmType | null>(null);
  const [modInfo, setModInfo] = useState<
    "" | "APPROVE_OFF" | "CHANGE_OFF" | "DUPLICATE_COLOR" | "DUPLICATE_EMP"
  >("");

  const isConfirmTemplateRef = useRef<boolean>(false);

  useEffect(() => {
    const divisionId =
      props.adminMenuDivisions?.all[ADMIN_NAME_LIST["Work Schedule"]]?.ids[
        props.selectedDivision?.id
      ];

    props.runSequence({ sequence: "SetupWorkSchedule", divisionId, restart: true });

    return () => {
      props.runSequence({ sequence: "SetupWorkSchedule", clear: true });
    };
  }, []);

  useEffect(() => {
    if (!isConfirmTemplateRef.current) {
      return;
    }

    console.log("selectedTemplate:", selectedTemplate);

    isConfirmTemplateRef.current = false;

    if (modConfirm?.data?.event) {
      handleSelectSlot(modConfirm.data.event);

      setSelectedTemplate(modConfirm.data.original);
    }

    handleCloseModConfirm();
  }, [selectedTemplate]);

  const timeShifts = useMemo(() => {
    const yearMonth = viewMonth.format("YYYY-MM");

    return props.SetupWorkScheduleSequence?.workShifts?.[yearMonth] ?? [];
  }, [props.SetupWorkScheduleSequence?.workShifts, viewMonth]);

  const filteredTimeShifts = useMemo(() => {
    const filter = props.SetupWorkScheduleSequence?.filter ?? {};

    return timeShifts.filter((item) => !filter.employee || item.employee === filter.employee);
  }, [
    props.SetupWorkScheduleSequence?.filter,
    props.SetupWorkScheduleSequence?.workShifts,
    timeShifts,
    viewMonth,
  ]);

  const holidays = useMemo(() => {
    const workShiftSettings = props.SetupWorkScheduleSequence?.workShiftSettings ?? [];
    const yearMonth = viewMonth.format("YYYY-MM");
    const [year, month] = yearMonth.split("-");

    const index = workShiftSettings.findIndex(
      (item) => item.ad_year === Number(year) && item.month === Number(month)
    );

    if (index === -1) {
      return { index, items: [] };
    }

    return { index, items: workShiftSettings[index].holiday };
  }, [props.SetupWorkScheduleSequence?.workShiftSettings, viewMonth]);

  const filteredHolidays = useMemo(() => holidays.items.filter((item) => item.active), [holidays]);

  const listDaysOfMonth: Moment[] = useMemo(() => getDaysOfMonth(viewMonth), [viewMonth]);

  const listMonthsOfYear: Moment[] = useMemo(() => {
    const months: Moment[] = [];

    const startMonth = viewMonth.clone().add(-1, "years");
    const endMonth = viewMonth.clone().add(-1, "months");

    const tempMonth = startMonth.clone().startOf("month");

    while (tempMonth.isSameOrBefore(endMonth, "month")) {
      months.push(tempMonth.clone());
      tempMonth.add(1, "month");
    }

    return months;
  }, [viewMonth]);

  const monthYearOptions = useMemo(() => {
    const workShiftSettings = props.SetupWorkScheduleSequence?.workShiftSettings ?? [];

    const availableMonthsFromSettings = workShiftSettings
      .filter((item) => item.id)
      .map((item) => moment(`${item.ad_year}-${item.month}`));

    // กรองเฉพาะเดือนที่มีใน workShiftSettings
    const filteredMonths = listMonthsOfYear.filter((month) =>
      availableMonthsFromSettings.find((settingMonth) => settingMonth.isSame(month, "months"))
    );

    return filteredMonths.map((month) => ({
      key: month.format("YYYY-MM"),
      text: month.format("MMMM YYYY"),
      value: month.format("YYYY-MM"),
    }));
  }, [listMonthsOfYear, props.SetupWorkScheduleSequence?.workShiftSettings]);

  const timeShiftTemplateOptions = useMemo(() => {
    const templates = props.SetupWorkScheduleSequence?.workShiftTemplates ?? [];

    return templates.map((item) => {
      const startTime = moment(item.start_time, ["HH:mm:ss", "HH:mm"]);
      const endTime = moment(item.end_time, ["HH:mm:ss", "HH:mm"]);

      return {
        key: item.id,
        text: `[${item.name}] ${startTime.format("HH:mm")} - ${endTime.format("HH:mm")}`,
        value: item.id,
      };
    });
  }, [props.SetupWorkScheduleSequence?.workShiftTemplates]);

  const enabledAction = useMemo(() => {
    const divisionId = props.SetupWorkScheduleSequence?.filter?.division;
    const workShiftSetting = props.SetupWorkScheduleSequence?.workShiftSettings?.[0];

    return !!divisionId && !!workShiftSetting;
  }, [props.SetupWorkScheduleSequence?.filter, props.SetupWorkScheduleSequence?.workShiftSettings]);

  const payrollRecordEmployeeIds = useMemo(() => {
    const payrollRecords = props.SetupWorkScheduleSequence?.payrollRecordEmployeeIds ?? {};
    const yearMonth = viewMonth.format("YYYY-MM");

    return payrollRecords[yearMonth] ?? [];
  }, [props.SetupWorkScheduleSequence?.payrollRecordEmployeeIds, viewMonth]);

  const handleChangeEditMode = (event: React.MouseEvent<HTMLButtonElement>, data: ButtonProps) => {
    setMode(data.name as EditModeType);
    setSelectedTemplate(null);
    setSelectedDuplicateEmployee(null);
    setAssignType("");

    if (data.name === "ADD") {
      setAssignType("TEMPLATE");
    } else if (data.name === "DUPLICATE") {
      setSelectedDuplicateEmployee({ type: "EMPLOYEE_ONLY", viewMonth });
    } else if (data.name === "APPROVE") {
      setModConfirm({ data: null, type: "APPROVE" });
    }
  };

  const handleClickAddEmployee = () => {
    setAddEmployeeMode(addEmployeeMode ? "" : "EMPLOYEE");
    setSelectedEmployee(null);
    setSelectedDepartmentId(null);
    setIsSearchByAffiliate(true);
  };

  const handleEditHoliday = (result: HolidayResult) => {
    const workShiftSettings = props.SetupWorkScheduleSequence?.workShiftSettings ?? [];

    const prepare: HolidaysType[] = result.targetDates.map((item) => ({
      active: true,
      allDay: true,
      end: item.clone(),
      resource: result.detail,
      start: item.clone(),
      title: HOLIDAY_TAG,
    }));

    console.log("Action:", result.action);
    console.log("Prepare:", prepare);

    if (holidays.index === -1) {
      return;
    }

    if (result.action === "ADD") {
      // เพิ่มวันหยุดใหม่
      workShiftSettings[holidays.index].holiday = [...holidays.items, ...prepare];
    } else {
      // ตั้งค่า active เป็น false สำหรับวันหยุดที่ตรงกับ targetDates
      const updatedHolidays = holidays.items.map((holiday) => {
        const shouldDeactivate = result.targetDates.some(
          (targetDate) => holiday.title === HOLIDAY_TAG && holiday.start.isSame(targetDate, "day")
        );

        return shouldDeactivate ? { ...holiday, active: false } : holiday;
      });

      workShiftSettings[holidays.index].holiday = updatedHolidays;
    }

    props.setProp("SetupWorkScheduleSequence.workShiftSettings", [...workShiftSettings]);
  };

  const handleOpenModRemove = (id: number) => {
    setModRemoveEmployee(id);
  };

  const handleCloseModRemoveEmployee = () => {
    setModRemoveEmployee(null);
  };

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    props.setProp("SetupWorkScheduleSequence.filter", {
      ...props.SetupWorkScheduleSequence?.filter,
      [name]: data.value,
    });
  };

  const handleSearch = (searchMonth: Moment, isRefresh = false, isConfirm = false) => {
    if (isRefresh && !isConfirm) {
      const storeSlots = props.SetupWorkScheduleSequence?.storeSlots ?? {};

      const allStoreSlotsFromAllMonths = Object.values(storeSlots).flat();

      // ถ้ามีการแก้ไข ให้แสดง confirmation dialog ก่อน refresh
      if (allStoreSlotsFromAllMonths.length > 0) {
        setModConfirm({ data: null, type: "SEARCH" });

        return;
      }
    }

    props.runSequence({
      sequence: "SetupWorkSchedule",
      action: ACTIONS.SEARCH,
      refreshMode: isRefresh ? "all" : undefined,
      viewMonth: searchMonth,
    });
  };

  const isSameEvent = (event1: EventsTimeShiftType, event2: EventsTimeShiftType) =>
    event1.start.isSame(event2.start) && event1.end.isSame(event2.end);

  const getTimeShiftsAndIndex = (employeeId: number) => {
    const foundIndex = timeShifts.findIndex((item) => item.employee === employeeId);

    return { foundIndex };
  };

  const handleAddSlotOff = (data: AddSlotOffType, isConfirm?: boolean) => {
    const { employee, index, startDate } = data;

    const events = timeShifts[index].events;

    // หา events ที่อยู่ในวันเดียวกันกับวันที่เลือก
    const filteredEvents = events.filter((event) => event.start.isSame(moment(startDate), "day"));

    // ถ้าวันนั้นเป็น OFF อยู่แล้ว ไม่ต้องทำอะไร
    if (filteredEvents[0]?.shift_status === "O") {
      return;
    }

    const hasLeave = filteredEvents.some((event) => event.leave_option);

    // หากวันนั้นมีช่วงเวลาทำงานอยู่แล้ว และยังไม่ได้ confirm และไม่มีการลา
    // แสดง modal เตือน
    if (filteredEvents.length > 0 && !isConfirm && !hasLeave) {
      setModConfirm({ data, type: "SLOT_OFF" });

      return;
    }

    // หากมีวันลาจะไม่สามารถ off ได้
    if (filteredEvents.length > 0 && hasLeave) {
      setModInfo("CHANGE_OFF");

      return;
    }

    const eventData = {
      employee,
      end: moment(`${startDate} 23:59`),
      shift_status: "O" as const,
      start: moment(`${startDate} 00:00`),
      title: "",
    };

    // ลบ events ทั้งหมดในวันนั้นออก (เพื่อแทนที่ด้วย OFF)
    const excludedEvents = events.filter((event) => !event.start.isSame(moment(startDate), "day"));

    // อัพเดท events ใหม่ (ลบ events เก่า + เพิ่ม OFF)
    timeShifts[index].events = excludedEvents;
    timeShifts[index].events.push(eventData);

    // เก็บเฉพาะ events ที่ไม่ใช่ OFF (สำหรับกรณี undo)
    const undoEvents = filteredEvents
      .filter((event) => event.shift_status !== "O")
      .map((event) => ({ ...event, deleted: !!event.id }));

    handleUpdateTimeShift(timeShifts, {
      slots: [{ action: "OFF", data: timeShifts[index], event: eventData, events: undoEvents }],
      type: "ADD",
    });
  };

  const handleUpdateTimeShift = (
    updatedTimeShifts: WorkShiftType[],
    slotAction: {
      slots: StoreSlotType[];
      type: "ADD" | "UPDATE";
      settings?: WorkShiftSettingType[];
    }
  ) => {
    const yearMonth = viewMonth.format("YYYY-MM");
    const storeSlots = props.SetupWorkScheduleSequence?.storeSlots ?? {};
    const workShifts = props.SetupWorkScheduleSequence?.workShifts ?? {};
    const workShiftSettings = props.SetupWorkScheduleSequence?.workShiftSettings ?? [];

    storeSlots[yearMonth] =
      slotAction.type === "UPDATE"
        ? [...slotAction.slots]
        : [...(storeSlots[yearMonth] ?? []), ...slotAction.slots];

    props.setProp("SetupWorkScheduleSequence", {
      ...props.SetupWorkScheduleSequence,
      storeSlots: { ...storeSlots },
      workShifts: { ...workShifts, [yearMonth]: updatedTimeShifts },
      workShiftSettings: slotAction.settings ?? workShiftSettings,
    });
  };

  const showTemplateConfirm = (
    filteredTemplate: WorkShiftTemplateType[],
    canEdit: boolean,
    eventData: SlotInfoType
  ) => {
    // เงื่อนไข: Custom template + มี template ตรงกัน > 1 + แก้ไขได้ + ยังไม่ confirm
    if (
      !selectedTemplate?.id &&
      filteredTemplate.length > 1 &&
      canEdit &&
      !selectedTemplate?.isConfirmed
    ) {
      setModConfirm({
        data: {
          event: eventData,
          original: { ...selectedTemplate },
          templates: filteredTemplate,
          value: filteredTemplate[0].id,
        },
        type: "TEMPLATE",
      });

      return true; // แสดง confirmation แล้ว
    }

    return false; // ไม่ต้องแสดง confirmation
  };

  const handleSelectSlot = (slotInfo: SlotInfoType) => {
    const workShiftTemplates = props.SetupWorkScheduleSequence?.workShiftTemplates ?? [];
    const startDate = slotInfo.start.format("YYYY-MM-DD");

    const { foundIndex } = getTimeShiftsAndIndex(slotInfo.data?.employee);

    if (foundIndex === -1) {
      return;
    }

    // หากเลือกประเภท "OFF" ให้เรียกฟังก์ชัน
    if (assignType === "OFF") {
      handleAddSlotOff({ employee: slotInfo.data?.employee, index: foundIndex, startDate });

      return;
    }

    // ตรวจสอบว่ามี template ที่เลือกและมีเวลาที่กำหนดหรือไม่
    if (!selectedTemplate?.start_time || !selectedTemplate.end_time) {
      return;
    }

    // หา template ที่ตรงกับเวลาที่เลือก
    // หากพบ template ที่มีเวลาเดียวกัน ให้ใช้ template นั้น (เพื่อใช้ข้อมูลที่สมบูรณ์)
    // หากไม่พบ ให้ใช้ selectedTemplate (custom time)
    const filteredTemplate = workShiftTemplates.filter((template) => {
      const isIdMatch = template.id === selectedTemplate.id;
      const isTimeMatch =
        template.start_time === selectedTemplate.start_time &&
        template.end_time === selectedTemplate.end_time;

      return selectedTemplate.id ? isIdMatch : isTimeMatch;
    });

    let events = timeShifts[foundIndex].events;
    let steps = 1;

    const template = filteredTemplate[0] || selectedTemplate;
    const startTime = moment(`${startDate} ${template.start_time}`);

    const eventData: EventsTimeShiftType = {
      code: template.code,
      color_code: template.color_code?.toLowerCase(),
      employee: slotInfo.data?.employee,
      end: moment(`${startDate} ${template.end_time}`),
      end_break_time: template.end_break_time,
      is_break_time: template.is_break_time,
      shift_status: "W",
      shift_type: template.shift_type,
      start: startTime,
      start_break_time: template.start_break_time,
      template: template.id,
      title: template.name,
    };

    const dailyEvents = events.filter((event) => event.start.isSame(startTime, "day"));
    const hasApprovedOffShift = dailyEvents.some(
      (event) => event.shift_status === "O" && event.approval_status === "A"
    );

    // หากมี slot off ที่ approve แล้วและมีการ add slot ให้แจ้งเตือน
    if (hasApprovedOffShift) {
      setModInfo("APPROVE_OFF");

      return;
    }

    // ตรวจสอบว่ามี event เดียวกันอยู่แล้วหรือไม่
    const foundEvent = events.find((item) => isSameEvent(item, eventData));
    const isApproved = foundEvent?.approval_status === "A";
    const canEditEvent = foundEvent ? !isApproved : true;

    // ตรวจสอบและแสดง template confirmation
    if (showTemplateConfirm(filteredTemplate, canEditEvent, slotInfo)) {
      return;
    }

    // เช็คใช้สีเดียวกันในวัน
    const hasColorInEvents = dailyEvents.some((event) => event.color_code === eventData.color_code);
    // เช็คใช้สีเดียวกันใน Template
    const hasColorInTemplates = workShiftTemplates.some(
      (item) => item.color_code === eventData.color_code
    );
    const isDuplicateColor = hasColorInEvents || (hasColorInTemplates && !selectedTemplate.id);

    if (isDuplicateColor && !foundEvent) {
      setModInfo("DUPLICATE_COLOR");

      return;
    }

    // หากมี event เดียวกันแล้ว
    if (foundEvent) {
      // หากเวลาตรงกันแต่ template คนละตัวกันให้แทนที่ slot เดิม
      if (selectedTemplate.id && selectedTemplate.id !== foundEvent.template && !isApproved) {
        steps++;

        handleDeleteSlot({ ...foundEvent, data: slotInfo.data }, foundIndex);
      } else {
        // ไม่ต้องเพิ่ม
        return;
      }
    }

    events = timeShifts[foundIndex].events;

    events.push(eventData);

    // อัพเดท events โดยลบ OFF ในวันเดียวกันออก (ถ้ามี)
    timeShifts[foundIndex].events = events.filter(
      (event) => event.shift_status !== "O" || !event.start.isSame(startTime, "day")
    );

    const undoEvents = events
      .filter((event) => event.shift_status === "O" && event.start.isSame(startTime, "day"))
      .map((event) => ({ ...event, deleted: !!event.id }));

    handleUpdateTimeShift(timeShifts, {
      slots: [{ action: "ADD", data: slotInfo.data, event: eventData, events: undoEvents, steps }],
      type: "ADD",
    });
  };

  const handleDeleteSlot = (event: EventsTimeShiftType, index: number) => {
    // หากเป็นการลาหรือมีการ Approve แล้วจะลบไม่ได้
    if (event.leave_option || event.approval_status === "A") {
      return;
    }

    // กรอง event ออกจาก array (ลบ event ที่เลือก)
    const events = timeShifts[index].events;

    const filteredEvents = events.filter((item) => !isSameEvent(item, event));

    timeShifts[index].events = filteredEvents;

    handleUpdateTimeShift(timeShifts, {
      slots: [{ action: "DELETE", data: event.data, event: { ...event, deleted: !!event.id } }],
      type: "ADD",
    });
  };

  const handleSelectEvent = (event: EventsTimeShiftType) => {
    const startTime = event.start.format("HH:mm");
    const endTime = event.end.format("HH:mm");
    const workShiftTemplates = props.SetupWorkScheduleSequence?.workShiftTemplates ?? [];

    const { foundIndex } = getTimeShiftsAndIndex(event.data?.employee);

    if (foundIndex === -1) {
      return;
    }

    // หากอยู่ในโหมด DELETE ให้ลบ slot ทันที
    if (mode === "DELETE") {
      handleDeleteSlot(event, foundIndex);

      return;
    }

    // เปรียบเทียบเวลาของ event กับ template ที่เลือก
    // หากเวลาไม่ตรงกัน ให้เลือก slot (highlight)
    if (selectedTemplate?.start_time !== startTime || selectedTemplate.end_time !== endTime) {
      handleSelectSlot(event);

      return;
    }

    // หากเป็น Custom และเวลาตรงกับ Ttemplate มากกว่า 1 ตัว
    const filteredTemplate = workShiftTemplates.filter(
      (template) =>
        template.start_time === selectedTemplate.start_time &&
        template.end_time === selectedTemplate.end_time
    );
    const isApproved = event.approval_status === "A";

    // ตรวจสอบและแสดง template confirmation
    if (showTemplateConfirm(filteredTemplate, !isApproved, event)) {
      return;
    }

    // หากเวลาตรงกันแต่ template คนละตัวกันให้แทนที่ slot เดิม
    if (selectedTemplate.id && selectedTemplate.id !== event.template) {
      handleSelectSlot(event);
    } else {
      // หากเวลาตรงกับ template ที่เลือก ให้ลบ slot
      // (กรณีคลิกซ้ำบน slot ที่มีเวลาเดียวกับ template)
      handleDeleteSlot(event, foundIndex);
    }
  };

  const undoSingleSlot = (
    slotToUndo: StoreSlotType,
    currentTimeShifts: WorkShiftType[],
    currentWorkShiftSettings: WorkShiftSettingType[]
  ) => {
    // หา index ของ employee ใน timeShifts
    const timeShiftData = getTimeShiftsAndIndex(slotToUndo.data?.employee);
    const { foundIndex } = timeShiftData;

    if (foundIndex === -1 && slotToUndo.action !== "DUPLICATE") {
      return {
        updatedTimeShifts: currentTimeShifts,
        workShiftSettings: currentWorkShiftSettings,
      };
    }

    let events = slotToUndo.action === "DUPLICATE" ? [] : currentTimeShifts[foundIndex]?.events;

    // ฟังก์ชันตรวจสอบว่า event มีเวลาเดียวกันกับ slotToUndo หรือไม่
    const isSameTimeSlot = (event: EventsTimeShiftType, granularity?: any) =>
      event.start.isSame(slotToUndo.event?.start, granularity) &&
      event.end.isSame(slotToUndo.event?.end, granularity);

    const restoredOffEvents = (slotToUndo.events ?? []).map((event) => ({
      ...event,
      deleted: false,
    }));

    // กรณี ADD: ลบ slot และ event ที่เพิ่มไป
    if (slotToUndo.action === "ADD") {
      events = events.filter((event) => !isSameTimeSlot(event));

      const filteredEvents = events.filter((event) => isSameTimeSlot(event, "day"));

      // ถ้ามี OFF เดิมและไม่มี events ในวันนั้น ให้คืน OFF กลับมา
      if (filteredEvents.length === 0) {
        events.push(...restoredOffEvents);
      }
    }
    // กรณี DELETE: คืน slot ที่ถูกลบกลับมา
    else if (slotToUndo.action === "DELETE") {
      if (slotToUndo.event) {
        events.push({ ...slotToUndo.event, deleted: false }); // เพิ่ม slot กลับเข้าไป
      }
    }
    // กรณี OFF: คืนสถานะเดิม
    else {
      events = events.filter((event) => !isSameTimeSlot(event));
      events.push(...restoredOffEvents); // คืน events เดิมกลับมา
    }

    let resultTimeShifts;
    let resultWorkShiftSettings;

    // อัพเดท events ใหม่
    if (slotToUndo.action === "DUPLICATE") {
      resultTimeShifts = (slotToUndo.timeShifts ?? []).map((item) => ({
        ...item,
        events: item.events.map((acc) => ({ ...acc, deleted: false })),
      }));

      resultWorkShiftSettings = [...(slotToUndo.workShiftSettings ?? [])];
    } else {
      resultTimeShifts = [...currentTimeShifts];
      resultTimeShifts[foundIndex] = {
        ...resultTimeShifts[foundIndex],
        events,
      };
      resultWorkShiftSettings = currentWorkShiftSettings;
    }

    return {
      updatedTimeShifts: resultTimeShifts,
      workShiftSettings: resultWorkShiftSettings,
    };
  };

  const handleClickUndo = () => {
    const yearMonth = viewMonth.format("YYYY-MM");
    const storeSlots = props.SetupWorkScheduleSequence?.storeSlots ?? {};
    const currentMonthSlots = storeSlots[yearMonth] ?? [];
    const lastSlot = currentMonthSlots.slice(-1)[0]; // ดึง slot สุดท้าย

    // ตรวจสอบว่ามี slot ให้ undo หรือไม่
    if (currentMonthSlots.length === 0) {
      return;
    }

    const stepsToUndo = lastSlot.steps ?? 1; // จำนวน steps ที่ต้อง undo

    // ตรวจสอบว่ามี slots เพียงพอให้ undo หรือไม่
    if (currentMonthSlots.length < stepsToUndo) {
      return;
    }

    let updatedTimeShifts = [...timeShifts];
    let workShiftSettings = props.SetupWorkScheduleSequence?.workShiftSettings ?? [];

    // undo แต่ละ step ตามลำดับ (จากล่าสุดไปเก่าสุด)
    for (let step = 0; step < stepsToUndo; step++) {
      const slotToUndo = currentMonthSlots[currentMonthSlots.length - 1 - step];

      const result = undoSingleSlot(slotToUndo, updatedTimeShifts, workShiftSettings);

      updatedTimeShifts = result.updatedTimeShifts;
      workShiftSettings = result.workShiftSettings;
    }

    // ลบ slots ที่ undo ไปแล้ว
    for (let index = 0; index < stepsToUndo; index++) {
      storeSlots[yearMonth]?.pop();
    }

    handleUpdateTimeShift(updatedTimeShifts, {
      settings: workShiftSettings,
      slots: storeSlots[yearMonth] ?? [],
      type: "UPDATE",
    });
  };
  const handleApprove = () => {
    if (modConfirm?.type === "SLOT_OFF") {
      if (modConfirm.data) {
        handleAddSlotOff(modConfirm.data as AddSlotOffType, true);

        handleCloseModConfirm();
      }
    } else if (modConfirm?.type === "DUPLICATE" && selectedDuplicateEmployee) {
      props.runSequence({
        sequence: "SetupWorkSchedule",
        action: ACTIONS.DUPLICATE,
        ...selectedDuplicateEmployee,
        confirm: true,
        onSuccess: handleCloseModConfirm,
      });
    } else if (modConfirm?.type === "APPROVE") {
      props.runSequence({
        sequence: "SetupWorkSchedule",
        action: ACTIONS.ALL_APPROVE,
        viewMonth,
        onSuccess: () => {
          handleCloseModConfirm();

          setMode("SELECT");
        },
      });
    } else if (modConfirm?.type === "SEARCH") {
      handleSearch(viewMonth, true, true);

      handleCloseModConfirm();
    } else if (modConfirm?.type === "TEMPLATE") {
      const templates: WorkShiftTemplateType[] = modConfirm.data?.templates || [];
      const foundTemplate = templates.find((template) => template.id === modConfirm.data?.value);

      if (foundTemplate) {
        isConfirmTemplateRef.current = true;

        setSelectedTemplate({ ...foundTemplate, isConfirmed: true });
      }
    }
  };

  const handleApplyDuplicate = () => {
    const length = timeShifts.flatMap((item) => item.events).length;

    // กรณีเดือนที่ต้องการ Duplicate Slot ทั้งหมด เป็น Blank ระบบจะทำการคัดลอกสำเร็จตามประเภทที่เลือกเลย
    if (length === 0 && selectedDuplicateEmployee) {
      props.runSequence({
        sequence: "SetupWorkSchedule",
        action: ACTIONS.DUPLICATE,
        ...selectedDuplicateEmployee,
      });
    } else {
      setModConfirm({ data: selectedDuplicateEmployee, type: "DUPLICATE" });
    }
  };

  const handleChangeTemplate = (e: SyntheticEvent | null, data: any) => {
    const name = data.name as keyof WorkShiftTemplateType;
    const value = data.type === "checkbox" ? data.checked : data.value;

    if (selectedTemplate) {
      setSelectedTemplate({ ...selectedTemplate, [name]: value });
    }
  };

  const handleChangeAssignType = (e: SyntheticEvent, data: DropdownProps) => {
    setAssignType(data.value as AssignType);
    setSelectedTemplate(null);

    if (data.value === "CUSTOM") {
      setSelectedTemplate({ ...DEFAULT_TEMPLATE });
    }
  };

  const handleChangeDuplicateEmployee = (e: SyntheticEvent, data: any) => {
    const name = data.name as "month" | "type";

    if (selectedDuplicateEmployee) {
      setSelectedDuplicateEmployee({ ...selectedDuplicateEmployee, [name]: data.value });
    }
  };

  const handleCloseModConfirm = () => {
    setModConfirm(null);
  };

  const handleOpenHistory = (data: OpenHistoryParamsType) => {
    props.runSequence({ sequence: "SetupWorkSchedule", action: ACTIONS.OPEN_HISTORY, ...data });
  };

  const handleCloseModHistory = () => {
    props.setProp("SetupWorkScheduleSequence.workShiftHistory", null);
  };

  const handleAddEmployee = () => {
    const workShifts = props.SetupWorkScheduleSequence?.workShifts ?? {};
    const employeeId = selectedEmployee?.id;
    const yearMonth = viewMonth.format("YYYY-MM");

    workShifts[yearMonth] ??= [];

    // ตรวจสอบว่าพนักงานคนนี้มีอยู่ในเดือนนี้แล้วหรือไม่
    const foundEmployee = workShifts[yearMonth]?.find((item) => item.employee === employeeId);

    if (foundEmployee) {
      setModInfo("DUPLICATE_EMP");

      return;
    }

    props.runSequence({
      sequence: "SetupWorkSchedule",
      action: ACTIONS.ADD_EMPLOYEE,
      departmentId: selectedDepartmentId,
      employeeData: selectedEmployee,
      viewMonth,
    });

    setSelectedEmployee(null);
  };

  const handleConfirmRemoveEmployee = () => {
    if (typeof modRemoveEmployee === "number") {
      props.runSequence({
        sequence: "SetupWorkSchedule",
        action: ACTIONS.REMOVE,
        employeeId: modRemoveEmployee,
        viewMonth,
        onSuccess: handleCloseModRemoveEmployee,
      });
    }
  };

  const handleSave = (type: ActionSaveType) => () => {
    props.runSequence({ sequence: "SetupWorkSchedule", action: ACTIONS.SAVE, type, viewMonth });
  };

  const handleCloseModInfoChangeOff = () => {
    if (modInfo === "DUPLICATE_EMP") {
      setSelectedEmployee(null);
    }

    setModInfo("");
  };

  const handleChangeViewMonth = (value: Moment) => {
    setViewMonth(value);

    handleSearch(value);

    if (selectedDuplicateEmployee) {
      setSelectedDuplicateEmployee({ ...selectedDuplicateEmployee, viewMonth: value });
    }
  };

  const handleGetClockRecord = async (data: ClockRecordParamsType) => {
    props.runSequence({
      sequence: "SetupWorkSchedule",
      action: ACTIONS.CLOCK_RECORD,
      ...data,
    });
  };

  const handleApplyNewTime = (data: ApplyNewTimeParamsType) => {
    props.runSequence({ sequence: "SetupWorkSchedule", action: ACTIONS.APPLY_NEW_TIME, ...data });
  };

  const handleApproveTime = (data: ApproveTimeParamsType) => {
    props.runSequence({ sequence: "SetupWorkSchedule", action: ACTIONS.APPROVE_TIME, ...data });
  };

  const handleEditRegularWork = (data: EditRegularWorkParams) => {
    props.runSequence({
      sequence: "SetupWorkSchedule",
      action: ACTIONS.EDIT_REGULAR_WORK,
      ...data,
      viewMonth,
    });
  };

  const handleEmployeeAttendanceReport = () => {
    props.runSequence({
      sequence: "SetupWorkSchedule",
      action: ACTIONS.EMPLOYEE_ATTENDANCE_REPORT,
      viewMonth,
    });
  };

  const handlePrintWorkSchedule = () => {
    props.runSequence({
      sequence: "SetupWorkSchedule",
      action: ACTIONS.PRINT_WORK_SCHEDULE,
      viewMonth,
    });
  };

  const handleToggleShowSummaryColumn = () => {
    setShowSummaryColumn(!showSummaryColumn);
  };

  const handleChangeAddEmployeeMode = (e: SyntheticEvent, data: RadioProps) => {
    setAddEmployeeMode(data.value as AddEmployeeModeType);
    setSelectedDepartmentId(null);
    setSelectedEmployee(null);
  };

  return (
    <div id="card-setup-work-schedule">
      <SnackMessage
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      {isViewShiftCodeSummary && (
        <LeaveTypeTooltip asTooltip={!!addEmployeeMode || ["ADD", "DUPLICATE"].includes(mode)} />
      )}

      <Form className="--override-disabled --grey" size="tiny">
        {/* "division-section" */}
        <FormGroup style={{ alignItems: "flex-start" }} inline>
          <FormField width={3} inline>
            <label>Division</label>
            <Dropdown
              id="CardSetupWorkSchedule-Dropdown-Division"
              style={{ width: "100%" }}
              name="division"
              value={props.SetupWorkScheduleSequence?.filter?.division ?? ""}
              fluid
              search
              selection
              options={props.divisionOptions}
              onChange={handleChangeFilter}
            />
          </FormField>
          <FormField width={3} inline>
            <div style={{ width: "100%" }}>
              <SearchBoxWithKey
                onEvent={props.onEvent}
                id="SWSS"
                selectedItem={props.SetupWorkScheduleSequence?.filter?.employee}
                type="UserEmployee"
                inline
                searchedItemListWithKey={props.searchedItemListWithKey}
                setSelected={(value) => {
                  props.setProp("SetupWorkScheduleSequence.filter.employee", value);
                }}
              />
            </div>
          </FormField>
          <FormField>
            <ButtonLoadCheck
              setProp={props.setProp}
              id="CardSetupWorkSchedule-ButtonLoadCheck-Search"
              color="blue"
              disabled={!props.SetupWorkScheduleSequence?.filter?.division}
              paramKey={BTN_ACTS.SEARCH}
              size="tiny"
              title="SEARCH"
              fluid
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
              onClick={() => {
                handleSearch(viewMonth, true);
              }}
            />
          </FormField>
          <FormField>
            <Button
              id="CardSetupWorkSchedule-Button-AddEmployeeMode"
              style={{ display: "flex", justifyContent: "space-between", width: "10rem" }}
              basic={!addEmployeeMode}
              color="blue"
              disabled={!enabledAction}
              size="tiny"
              icon
              onClick={handleClickAddEmployee}
            >
              <Icon name="add user" /> <label>Employee</label>
              <label />
            </Button>
          </FormField>
          {!!addEmployeeMode && (
            <FormField style={{ display: "grid" }} width={7}>
              <FormGroup style={{ marginBottom: "0.5rem", width: "100%" }} inline>
                <FormField>
                  <Radio
                    style={{ fontWeight: "bold", minWidth: "10.55rem" }}
                    checked={addEmployeeMode === "EMPLOYEE"}
                    label="Select Employee"
                    value="EMPLOYEE"
                    onChange={handleChangeAddEmployeeMode}
                  />
                </FormField>
                <FormField width={6}>
                  <SearchBoxWithKey
                    onEvent={props.onEvent}
                    id="SWSA"
                    disabled={addEmployeeMode !== "EMPLOYEE"}
                    placeholder="Search Employee"
                    selectedItem={selectedEmployee?.id}
                    type="UserEmployee"
                    inline
                    searchedItemListWithKey={props.searchedItemListWithKey}
                    setSelected={(value, key, data) => {
                      setSelectedEmployee(data);
                    }}
                    onAdditionalUrlParams={() => ({
                      affiliate: isSearchByAffiliate
                        ? props.SetupWorkScheduleSequence?.filter?.division
                        : undefined,
                    })}
                  />
                </FormField>
                <FormField>
                  <Checkbox
                    id="CardSetupWorkSchedule-Checkbox-IsAffiliate"
                    checked={isSearchByAffiliate}
                    label="Affiliate"
                    onChange={(e, data) => {
                      setIsSearchByAffiliate(Boolean(data.checked));
                    }}
                  />
                </FormField>
              </FormGroup>
              <FormGroup style={{ marginBottom: "-0.5rem", width: "100%" }} inline>
                <FormField>
                  <Radio
                    style={{ fontWeight: "bold", minWidth: "10.55rem" }}
                    checked={addEmployeeMode === "DEPARTMENT"}
                    label="Select Department"
                    value="DEPARTMENT"
                    onChange={handleChangeAddEmployeeMode}
                  />
                </FormField>
                <FormField width={6}>
                  <Dropdown
                    className="inline-label"
                    style={{ width: "100%" }}
                    disabled={addEmployeeMode !== "DEPARTMENT"}
                    placeholder="Search Division"
                    value={selectedDepartmentId ?? ""}
                    fluid
                    search
                    selection
                    options={props.divisionOptions}
                    onChange={(e, data) => {
                      setSelectedDepartmentId(Number(data.value));
                    }}
                  />
                </FormField>
                <FormField>
                  <Button
                    id="CardSetupWorkSchedule-Button-AddEmployee"
                    color="green"
                    disabled={!selectedEmployee && !selectedDepartmentId}
                    loading={!!props.buttonLoadCheck?.[BTN_ACTS.ADD_EMPLOYEE]}
                    size="tiny"
                    onClick={handleAddEmployee}
                  >
                    ADD
                  </Button>
                </FormField>
              </FormGroup>
            </FormField>
          )}
        </FormGroup>
      </Form>
      <div id="control-section">
        {/* "mode-section" */}
        <Form id="division-section" className="--override-disabled --grey" size="tiny">
          {/* "division-section" */}
          <FormGroup inline>
            <FormField>
              <label style={{ minWidth: "max-content" }}>Mode : </label>
            </FormField>
            <FormField>
              <ButtonMode
                currentMode={mode}
                mode="SELECT"
                onChangeEditMode={handleChangeEditMode}
              />
            </FormField>
            <FormField>
              <ButtonMode currentMode={mode} mode="ADD" onChangeEditMode={handleChangeEditMode} />
            </FormField>
            <FormField>
              <ButtonMode
                currentMode={mode}
                mode="DELETE"
                onChangeEditMode={handleChangeEditMode}
              />
            </FormField>
            <FormField>
              <ButtonMode
                currentMode={mode}
                mode="DUPLICATE"
                onChangeEditMode={handleChangeEditMode}
              />
            </FormField>
            <FormField>
              <ButtonMode
                currentMode={mode}
                disabled={isViewShiftCodeSummary}
                mode="APPROVE"
                onChangeEditMode={handleChangeEditMode}
              />
            </FormField>

            {mode === "ADD" && (
              <>
                <FormField style={{ minWidth: "13.25rem" }} width={1} inline>
                  <label style={{ minWidth: "max-content" }}>Assign Shift time</label>
                  <Dropdown
                    id="CardSetupWorkSchedule-Dropdown-AssignType"
                    className="inline-label"
                    style={{ width: "100%" }}
                    value={assignType}
                    fluid
                    selection
                    options={ASSIGN_SHIFT_TIME_OPTIONS}
                    onChange={handleChangeAssignType}
                  />
                </FormField>
                {assignType === "TEMPLATE" && (
                  <FormField width={2} inline>
                    <Dropdown
                      id="CardSetupWorkSchedule-Dropdown-WorkShiftTemplate"
                      className="inline-label"
                      style={{ width: "100%" }}
                      value={selectedTemplate?.id ?? ""}
                      fluid
                      search
                      selection
                      options={timeShiftTemplateOptions}
                      onChange={(e, data) => {
                        const workShiftTemplates =
                          props.SetupWorkScheduleSequence?.workShiftTemplates ?? [];
                        const foundItem = workShiftTemplates.find(
                          (item) => item.id === Number(data.value)
                        );

                        setSelectedTemplate(foundItem ?? null);
                      }}
                    />
                  </FormField>
                )}
                {assignType === "CUSTOM" && (
                  <>
                    <FormField style={{ minWidth: "9.4rem" }} width={1} inline>
                      <label style={{ minWidth: "max-content" }}>Work time</label>
                      <TimeComboBox
                        id="start_time"
                        style={{ width: "100%" }}
                        defaultValue={selectedTemplate?.start_time}
                        onTextChange={(value) => {
                          handleChangeTemplate(null, { name: "start_time", value });
                        }}
                      />
                    </FormField>
                    <FormField style={{ minWidth: "6.4rem" }} width={1} inline>
                      <label style={{ minWidth: "max-content" }}>To</label>

                      <TimeComboBox
                        id="end_time"
                        style={{ width: "100%" }}
                        defaultValue={selectedTemplate?.end_time}
                        onTextChange={(value) => {
                          handleChangeTemplate(null, { name: "end_time", value });
                        }}
                      />
                    </FormField>
                    <FormField inline>
                      <Checkbox
                        id="CardSetupWorkSchedule-Checkbox-IsBreakTime"
                        style={{
                          fontWeight: "bold",
                          margin: "0 -1rem 0 -0.65rem",
                          minWidth: "max-content",
                          transform: "scale(0.8)",
                        }}
                        checked={selectedTemplate?.is_break_time ?? false}
                        label="Break time"
                        name="is_break_time"
                        onChange={handleChangeTemplate}
                      />
                    </FormField>
                    <FormField style={{ minWidth: "5.4rem" }} width={1} inline>
                      <label style={{ minWidth: "max-content" }} />
                      <TimeComboBox
                        id="CardSetupWorkSchedule-TimeComboBox-StartBreakTime"
                        style={{ width: "100%" }}
                        defaultValue={selectedTemplate?.start_break_time || ""}
                        disabled={!selectedTemplate?.is_break_time}
                        onTextChange={(value) => {
                          handleChangeTemplate(null, { name: "start_break_time", value });
                        }}
                      />
                    </FormField>
                    <FormField style={{ minWidth: "6.4rem" }} width={1} inline>
                      <label style={{ minWidth: "max-content" }}>To</label>
                      <TimeComboBox
                        id="CardSetupWorkSchedule-TimeComboBox-EndBreakTime"
                        style={{ width: "100%" }}
                        defaultValue={selectedTemplate?.end_break_time || ""}
                        disabled={!selectedTemplate?.is_break_time}
                        onTextChange={(value) => {
                          handleChangeTemplate(null, { name: "end_break_time", value });
                        }}
                      />
                    </FormField>
                    <FormField>
                      <ColorPicker
                        value={selectedTemplate?.color_code}
                        onChange={(value) => {
                          handleChangeTemplate(null, { name: "color_code", value });
                        }}
                      />
                    </FormField>
                    <FormField style={{ marginTop: "-2px" }}>
                      <label style={{ minWidth: "max-content" }}>Shift type : </label>
                    </FormField>
                    <FormField>
                      <Radio
                        id="CardSetupWorkSchedule-Radio-Day"
                        style={{ margin: "0 -1rem 0 -0.5rem", transform: "scale(0.82)" }}
                        checked={selectedTemplate?.shift_type === "D"}
                        label="Day"
                        name="shift_type"
                        value="D"
                        onChange={handleChangeTemplate}
                      />
                    </FormField>
                    <FormField>
                      <Radio
                        id="CardSetupWorkSchedule-Radio-Night"
                        style={{ margin: "0 -0.5rem 0 0rem", transform: "scale(0.82)" }}
                        checked={selectedTemplate?.shift_type === "N"}
                        label="Night"
                        name="shift_type"
                        value="N"
                        onChange={handleChangeTemplate}
                      />
                    </FormField>
                  </>
                )}
                {assignType !== "CUSTOM" && (
                  <FormField
                    id="CardSetupWorkSchedule-FormField-AddInfo"
                    style={{ padding: "0 0.5rem" }}
                  >
                    Then, Click on each cell in the table to set the working hours
                  </FormField>
                )}
              </>
            )}

            {mode === "DELETE" && (
              <FormField
                id="CardSetupWorkSchedule-FormField-DeleteInfo"
                style={{ padding: "0 0.5rem" }}
              >
                Click on each shift time in the table to delete
              </FormField>
            )}

            {mode === "DUPLICATE" && (
              <>
                <FormField inline>
                  <Radio
                    id="CardSetupWorkSchedule-Radio-DupEmployee"
                    style={{ margin: "0 -0.5rem 0 0rem", transform: "scale(0.9)" }}
                    checked={selectedDuplicateEmployee?.type === "EMPLOYEE_ONLY"}
                    label="Duplicate employee list"
                    name="type"
                    value="EMPLOYEE_ONLY"
                    onChange={handleChangeDuplicateEmployee}
                  />
                </FormField>
                <FormField inline>
                  <Radio
                    id="CardSetupWorkSchedule-Radio-DupShiftTime"
                    style={{ margin: "0 -0.75rem 0 -0.5rem", transform: "scale(0.9)" }}
                    checked={selectedDuplicateEmployee?.type === "SHIFT_TIME"}
                    label="Duplicate employee list & Shift time"
                    name="type"
                    value="SHIFT_TIME"
                    onChange={handleChangeDuplicateEmployee}
                  />
                </FormField>
                <FormField width={2} inline>
                  <Dropdown
                    id="CardSetupWorkSchedule-Dropdown-DupYearMonth"
                    name="yearMonth"
                    value={selectedDuplicateEmployee?.yearMonth ?? ""}
                    search
                    selection
                    options={monthYearOptions}
                    onChange={handleChangeDuplicateEmployee}
                  />
                </FormField>
                <FormField inline>
                  <ButtonLoadCheck
                    setProp={props.setProp}
                    id="CardSetupWorkSchedule-ButtonLoadCheck-ApplyDuplicate"
                    style={{ marginLeft: "0.5rem" }}
                    color="green"
                    disabled={!selectedDuplicateEmployee?.yearMonth}
                    paramKey={BTN_ACTS.DUPLICATE}
                    size="small"
                    title="Apply"
                    fluid
                    buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.DUPLICATE]}
                    onClick={handleApplyDuplicate}
                  />
                </FormField>
              </>
            )}

            {["ADD", "DELETE", "DUPLICATE"].includes(mode) && (
              <FormField
                style={{ marginLeft: mode === "DUPLICATE" ? "0.75rem" : "", paddingRight: 0 }}
              >
                <Button
                  id="CardSetupWorkSchedule-Button-Undo"
                  size="tiny"
                  onClick={handleClickUndo}
                  disabled={
                    (
                      props.SetupWorkScheduleSequence?.storeSlots?.[viewMonth.format("YYYY-MM")] ??
                      []
                    ).length === 0
                  }
                >
                  Undo
                </Button>
              </FormField>
            )}
          </FormGroup>
        </Form>

        {/* "calendar-working" */}
        <Form size="tiny">
          <FormGroup style={{ marginBottom: "0.5rem" }} inline>
            <FormField inline>
              <SelectMonthYear
                isLoading={!!props.buttonLoadCheck?.[`${BTN_ACTS.SEARCH}_CHANGE`]}
                viewMonth={viewMonth}
                setViewMonth={handleChangeViewMonth}
              />
            </FormField>
            <FormField inline>
              <Button
                id="CardSetupWorkSchedule-Button-PreviousMonth"
                style={{
                  backgroundColor: "rgba(246, 246, 246, 1)",
                  border: "1px solid rgba(186, 184, 184, 1)",
                }}
                labelPosition="left"
                size="mini"
                icon
                onClick={() => {
                  handleChangeViewMonth(viewMonth.clone().subtract(1, "month"));
                }}
              >
                <Icon name="angle left" />
                Previous Month
              </Button>
            </FormField>
            <FormField inline>
              <Button
                id="CardSetupWorkSchedule-Button-NextMonth"
                style={{
                  backgroundColor: "rgba(246, 246, 246, 1)",
                  border: "1px solid rgba(186, 184, 184, 1)",
                }}
                labelPosition="right"
                size="mini"
                icon
                onClick={() => {
                  handleChangeViewMonth(viewMonth.clone().add(1, "month"));
                }}
              >
                Next Month
                <Icon name="angle right" />
              </Button>
            </FormField>
            <FormField inline>
              <Checkbox
                id="CardSetupWorkSchedule-Checkbox-IsViewShiftCodeSummary"
                checked={isViewShiftCodeSummary}
                label="view shift code summary"
                onChange={(e, data) => {
                  setIsViewShiftCodeSummary(!!data.checked);
                }}
              />
            </FormField>
            <FormField style={{ flex: 1 }} inline />
            <FormField style={{ padding: 0 }} inline>
              <Button
                id="CardSetupWorkSchedule-Button-ShowSummaryColumn"
                style={{ minWidth: "10rem" }}
                color="blue"
                size="tiny"
                icon
                onClick={handleToggleShowSummaryColumn}
              >
                {showSummaryColumn ? "Hide Work Time Summary" : "Show Work Time Summary"}
              </Button>
            </FormField>
          </FormGroup>
        </Form>
      </div>
      <div id="calender-working-shift" className={`mode-${mode}`}>
        <TableTimeShift
          clockRecords={props.SetupWorkScheduleSequence?.clockRecords}
          data={filteredTimeShifts}
          holidays={filteredHolidays}
          isViewShiftCodeSummary={isViewShiftCodeSummary}
          listMonths={listDaysOfMonth}
          payrollRecordEmployeeIds={payrollRecordEmployeeIds}
          selectedMode={mode}
          showSummaryColumn={isViewShiftCodeSummary ? false : showSummaryColumn}
          buttonLoadCheck={props.buttonLoadCheck}
          onApplyNewTime={handleApplyNewTime}
          onApproveTime={handleApproveTime}
          onEditHoliday={handleEditHoliday}
          onEditRegularWork={handleEditRegularWork}
          onGetClockRecord={handleGetClockRecord}
          onOpenHistory={handleOpenHistory}
          onRemoveEmployee={handleOpenModRemove}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
        />
      </div>

      <Form style={{ marginTop: "1rem" }} size="tiny">
        <FormGroup inline>
          <FormField style={{ flex: 1 }} />
          <FormField>
            <ButtonLoadCheck
              setProp={props.setProp}
              id="CardSetupWorkSchedule-ButtonLoadCheck-PrintEAReport"
              color="yellow"
              disabled={!enabledAction}
              icon="print"
              paramKey={BTN_ACTS.EMPLOYEE_ATTENDANCE_REPORT}
              size="tiny"
              title="Employee attendance report"
              fluid
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.EMPLOYEE_ATTENDANCE_REPORT]}
              onClick={handleEmployeeAttendanceReport}
            />
          </FormField>
          <FormField>
            <ButtonLoadCheck
              setProp={props.setProp}
              id="CardSetupWorkSchedule-ButtonLoadCheck-PrintWSReport"
              color="yellow"
              disabled={!enabledAction}
              icon="print"
              paramKey={BTN_ACTS.PRINT_WORK_SCHEDULE}
              size="tiny"
              title="PRINT WORK SCHEDULE"
              fluid
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PRINT_WORK_SCHEDULE]}
              onClick={handlePrintWorkSchedule}
            />
          </FormField>
          <FormField>
            <ButtonLoadCheck
              setProp={props.setProp}
              id="CardSetupWorkSchedule-ButtonLoadCheck-SaveShiftTime"
              color="blue"
              disabled={!enabledAction}
              paramKey={`${BTN_ACTS.SAVE}_NO_BLANK_OFF`}
              size="tiny"
              title="SAVE SHIFT TIME"
              fluid
              buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.SAVE}_NO_BLANK_OFF`]}
              onClick={handleSave("NO_BLANK_OFF")}
            />
          </FormField>
          <FormField>
            <ButtonLoadCheck
              setProp={props.setProp}
              id="CardSetupWorkSchedule-ButtonLoadCheck-ApplyBlankOff"
              color="green"
              disabled={!enabledAction}
              paramKey={`${BTN_ACTS.SAVE}_APPLY_BLANK_OFF`}
              size="tiny"
              title="SAVE & APPLY BLANK TO OFF"
              fluid
              buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.SAVE}_APPLY_BLANK_OFF`]}
              onClick={handleSave("APPLY_BLANK_OFF")}
            />
          </FormField>
        </FormGroup>
      </Form>

      <ModConfirmCNMI
        approveButtonColor="green"
        approveButtonLoading={props.buttonLoadCheck?.[BTN_ACTS.REMOVE]}
        approveButtonStyle={{ minWidth: "max-content" }}
        approveButtonText="CONFIRM DELETING"
        denyButtonText="CLOSE"
        modalStyle={{ width: "35rem" }}
        openModal={typeof modRemoveEmployee === "number"}
        titleColor="red"
        titleName="Alert message"
        onApprove={handleConfirmRemoveEmployee}
        onCloseWithDimmerClick={handleCloseModRemoveEmployee}
        onDeny={handleCloseModRemoveEmployee}
        content={
          <div
            style={{
              fontWeight: "bold",
              lineHeight: 2,
              margin: "-0.5rem 0 -1.5rem",
              textAlign: "center",
            }}
          >
            <div>Deleting this employee will remove all associated shift times.</div>
            <div>Would you confirm to delete this employee?</div>
          </div>
        }
      />

      <ModConfirmCNMI
        approveButtonText="CONFIRM"
        denyButtonText="CLOSE"
        modalStyle={{ width: "35rem" }}
        openModal={!!modConfirm}
        titleColor="red"
        titleName="Alert message"
        onApprove={handleApprove}
        onCloseWithDimmerClick={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
        approveButtonLoading={
          props.buttonLoadCheck?.[`${BTN_ACTS.DUPLICATE}_CONFIRM`] ||
          props.buttonLoadCheck?.[BTN_ACTS.ALL_APPROVE]
        }
        content={
          <div
            style={{
              fontWeight: "bold",
              lineHeight: 2,
              margin: "-0.5rem 0 -1.5rem",
              textAlign: "center",
            }}
          >
            {modConfirm?.type === "SLOT_OFF" && (
              <div>Would you confirm to remove the working hours and change to OFF</div>
            )}
            {modConfirm?.type === "DUPLICATE" && (
              <div>Would you confirm to duplicate the work schedule to selected month</div>
            )}
            {modConfirm?.type === "APPROVE" && (
              <div>Would you like to approve the whole work schedule</div>
            )}
            {modConfirm?.type === "SEARCH" && (
              <div>
                There are unsaved changes. If you confirm the search, all changes will be lost.
              </div>
            )}
            {modConfirm?.type === "TEMPLATE" && (
              <div>
                <div>There are existing shift time templates that match your current settings.</div>
                <div>Please select the template you wish to use.</div>

                {(modConfirm.data?.templates as Record<string, any>[]).map((item) => (
                  <div key={`template-${item.id}`} style={{ display: "flex", marginTop: "1rem" }}>
                    <Radio
                      style={{ marginLeft: "1.5rem" }}
                      checked={item.id === modConfirm.data?.value}
                      label={`[${item.name}] ${item.start_time} - ${item.end_time}`}
                      value={item.id}
                      onChange={(e, v) => {
                        setModConfirm({
                          ...modConfirm,
                          data: { ...modConfirm.data, value: v.value },
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        }
      />

      <ModInfo
        style={{ minWidth: "32rem" }}
        open={!!modInfo}
        titleColor="red"
        titleName="Alert message"
        onApprove={handleCloseModInfoChangeOff}
        onClose={handleCloseModInfoChangeOff}
      >
        <div
          style={{
            fontWeight: "bold",
            lineHeight: 2,
            margin: "0.5rem 0px 0.5rem",
          }}
        >
          {modInfo === "CHANGE_OFF" && (
            <>
              <div>Cannot change to off because leave records have been detected.</div>
              <div>Please proceed to reject the leave request first.</div>
            </>
          )}
          {modInfo === "DUPLICATE_EMP" && (
            <div>Duplicate Employee Code found. Please check the information before adding.</div>
          )}
          {modInfo === "APPROVE_OFF" && (
            <>
              <div>Unable to add data. This shift has already been approved.</div>
              <div>Please unapprove it first.</div>
            </>
          )}
          {modInfo === "DUPLICATE_COLOR" && (
            <div>This color code cannot be used because it is already in use.</div>
          )}
        </div>
      </ModInfo>

      <ModTimeShiftHistory
        items={props.SetupWorkScheduleSequence?.workShiftHistory}
        open={!!props.SetupWorkScheduleSequence?.workShiftHistory}
        onClose={handleCloseModHistory}
      />
    </div>
  );
};

CardSetupWorkSchedule.displayName = "CardSetupWorkSchedule";

export default React.memo(CardSetupWorkSchedule);
